/**
 * UpdateDetailsForm
 *
 * Form component for updating user's profile details.
 * - Preloads user details into local state.
 * - Integrates with Google Maps Places API for location autocomplete.
 * - Handles image upload and preview.
 * - Submits updated details via useUpdateDetails hook.
 * - Displays status messages for loading, errors, and success.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Autocomplete,
} from "@mui/material";
import { useLoadScript } from "@react-google-maps/api";
import { useUpdateDetails } from "../../hooks/useUpdateDetails";
import { getStaticAssetPath } from "../../utils/staticAssets";
import {
  getAutocompleteService,
  getPlacesService,
  getCityFromPlace,
  getCountryFromPlace,
} from "../../utils/googleMaps";
import { formContainer } from "./styles";
import { useProfileUpdateContext } from "../../contexts/ProfileUpdateContext";

export type UserDetailsData = {
  name?: string;
  last_name?: string;
  email?: string;
  location?: string;
  city?: string;
  country?: string;
  institute?: string;
  description?: string;
  image?: string;
};

type LocationOption = google.maps.places.AutocompletePrediction;

const LIBRARIES: "places"[] = ["places"];

/**
 * Main form component for updating user details.
 * @param userDetails - The current user details to preload into the form.
 */
export default function UpdateDetailsForm() {
  const { profileData, setProfileData } = useProfileUpdateContext();
  // Hook for API status and submission
  const { status, clearStatus, submitUpdateDetails } = useUpdateDetails();
  // Google Maps API script loader
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
    libraries: LIBRARIES,
  });

  // Local state for form fields, initialized from profileData
  const [data, setData] = useState({
    name: profileData?.name || "",
    last_name: profileData?.last_name || "",
    email: profileData?.email || "",
    location: profileData?.location || "",
    city: profileData?.city || "",
    country: profileData?.country || "",
    institute: profileData?.institute || "",
    description: profileData?.description || "",
    image: null as File | null,
  });

  // Local state for location autocomplete input and options
  const [locationInput, setLocationInput] = useState("");
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);

  /**
   * Memoized image preview URL.
   * Uses uploaded file if present, otherwise falls back to user's current image or a default.
   */
  const preview = useMemo(() => {
    if (data.image instanceof File) return URL.createObjectURL(data.image);

    if (profileData?.image) {
      if (profileData.image.startsWith("http")) {
        return profileData.image;
      }
      return getStaticAssetPath(profileData.image);
    }

    return getStaticAssetPath("images/recipeHero.png");
  }, [data.image, profileData?.image]);

  /**
   * Preload user details into form state when userDetails changes.
   */
  useEffect(() => {
    if (!profileData) return;
    setData((prev) => ({
      ...prev,
      name: profileData.name || "",
      last_name: profileData.last_name || "",
      email: profileData.email || "",
      location: profileData.location || "",
      city: profileData.city || "",
      country: profileData.country || "",
      institute: profileData.institute || "",
      description: profileData.description || "",
      image: null,
    }));
    setLocationInput(profileData.location || "");
    clearStatus();
  }, [profileData]);

  /**
   * Returns a change handler for a given field key.
   */
  const onChange =
    (k: keyof typeof data) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setData((d) => ({ ...d, [k]: e.target.value }));

  /**
   * Handles image file selection.
   * @param file - The selected image file or null to clear.
   */
  const onImage = (file: File | null) =>
    setData((d) => ({ ...d, image: file }));

  // Google Maps Autocomplete and Places services
  const autocompleteService = useMemo(
    () => getAutocompleteService(isLoaded),
    [isLoaded]
  );
  const placesService = useMemo(() => getPlacesService(isLoaded), [isLoaded]);

  // Session token for Google Places API autocomplete
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  /**
   * Gets or creates a session token for Google Places API.
   */
  const getSessionToken = () => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current =
        new google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  };

  /**
   * Fetches location predictions from Google Places Autocomplete API as user types.
   */
  useEffect(() => {
    if (!autocompleteService || !locationInput) {
      setLocationOptions([]);
      return;
    }
    const handle = setTimeout(() => {
      autocompleteService.getPlacePredictions(
        { input: locationInput, sessionToken: getSessionToken() },
        (predictions) => setLocationOptions(predictions || [])
      );
    }, 200);
    return () => clearTimeout(handle);
  }, [autocompleteService, locationInput]);

  /**
   * Handles selection of a location from the autocomplete dropdown.
   * Updates both location and city fields in form state.
   */
  const handleLocationSelect = (
    _: any,
    value: LocationOption | string | null
  ) => {
    if (!value) {
      setLocationInput("");
      setData((d) => ({ ...d, location: "", city: "", country: "" }));
      sessionTokenRef.current = null;
      return;
    }

    if (typeof value === "string") {
      setLocationInput(value);
      setData((d) => ({ ...d, location: value, city: "", country: "" })); // Add country: ""
      return;
    }

    if (!placesService) return;

    placesService.getDetails(
      {
        placeId: value.place_id,
        fields: ["address_components", "formatted_address"],
        sessionToken: getSessionToken(),
      },
      (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK && place) {
          setData((d) => ({
            ...d,
            location: place.formatted_address || value.description || "",
            city: getCityFromPlace(place),
            country: getCountryFromPlace(place), // Add this line
          }));
          setLocationInput(place.formatted_address || value.description || "");
        } else {
          setData((d) => ({
            ...d,
            location: value.description || "",
            city: "",
            country: "", // Add this line
          }));
          setLocationInput(value.description || "");
        }
        sessionTokenRef.current = null;
      }
    );
  };

  /**
   * Handles form submission.
   * Calls the send function from useUpdateDetails hook.
   */
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearStatus();
    // The result on success is the API body: { message: string, user: UserData }
    const result = await submitUpdateDetails(data);

    // Only update context if update was successful (result is truthy) and profileData exists
    if (result && profileData) {
      const updatedUser = (result as { user: UserDetailsData })?.user || {};

      setProfileData((prevProfile) => {
        if (!prevProfile) return null;

        const newImagePath =
          updatedUser.image && updatedUser.image !== ""
            ? updatedUser.image
            : prevProfile.image;

        return {
          ...prevProfile,
          name: updatedUser.name || data.name,
          last_name: updatedUser.last_name || data.last_name,
          email: updatedUser.email || data.email,
          location: updatedUser.location || data.location,
          city: updatedUser.city || data.city,
          country: updatedUser.country || data.country,
          institute: updatedUser.institute || data.institute,
          description: updatedUser.description || data.description,
          // Save the full, signed URL to context
          image: newImagePath,
        };
      });

      // Clear the local file state after successful upload
      onImage(null);
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit} sx={formContainer}>
      {/* Status messages */}
      {status.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {status.error}
        </Alert>
      )}
      {status.success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Profile updated successfully!
        </Alert>
      )}
      {status.loading && <CircularProgress sx={{ mb: 2 }} />}

      {/* Avatar row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
        <Avatar src={preview} sx={{ width: 80, height: 80 }} />
        <Box>
          <Typography fontWeight={700}>Profile Picture</Typography>
          <Button
            variant="outlined"
            component="label"
            sx={{ mt: 1 }}
            disabled={status.loading}
          >
            Select file
            <input
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => onImage(e.target.files?.[0] || null)}
            />
          </Button>
        </Box>
      </Box>

      {/* Name field */}
      <TextField
        label="Name (required)"
        value={data.name}
        onChange={onChange("name")}
        fullWidth
        required
        margin="normal"
      />
      {/* Last Name field */}
      <TextField
        label="Last Name (required)"
        value={data.last_name}
        onChange={onChange("last_name")}
        fullWidth
        required
        margin="normal"
      />
      {/* Email field */}
      <TextField
        label="Email address (required)"
        type="email"
        value={data.email}
        onChange={onChange("email")}
        fullWidth
        required
        margin="normal"
      />
      {/* Institute field */}
      <TextField
        label="Organisation or company (required)"
        value={data.institute}
        onChange={onChange("institute")}
        fullWidth
        required
        margin="normal"
      />

      {/* Location autocomplete field */}
      {isLoaded && (
        <Autocomplete
          freeSolo
          filterOptions={(x) => x}
          options={locationOptions}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.description || ""
          }
          inputValue={locationInput}
          onInputChange={(_, value, reason) => {
            if (reason === "input") getSessionToken();
            setLocationInput(value);
          }}
          onChange={handleLocationSelect}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Location (required)"
              placeholder="Enter your city/town..."
              fullWidth
              required
              margin="normal"
            />
          )}
        />
      )}

      {/* Description field */}
      <TextField
        label="About You"
        value={data.description}
        onChange={onChange("description")}
        fullWidth
        multiline
        minRows={3}
        margin="normal"
      />

      {/* Submit button */}
      <Button
        type="submit"
        variant="contained"
        sx={{ mt: 2 }}
        disabled={status.loading}
      >
        Update
      </Button>
    </Box>
  );
}
